import { pickDto } from '@/shared/api';
import { AdvertiserAccount } from '@synapex/shared2/gateway-rest-api';
import { SnakeIdObj } from '@synapex/tools/typescript';
import { patchObject } from '@synapex/tools/utils';

import {
  deleteUser,
  updateOwnerSettings,
  updateUser,
} from '../../api-client/users';
import {
  MutateOpts,
  UseMutationOpts,
  useMutationOptimistic,
} from '../../../engine';
import { users } from '../key-fn';

///////////////

export type UpdateOwnerSettingsOpts = MutateOpts<
  typeof updateOwnerSettings,
  typeof users
>;

export const useUpdateOwnerSettings = (
  p: SnakeIdObj<'advertiser_account'>,
  opts?: UseMutationOpts<UpdateOwnerSettingsOpts>,
) => {
  const queryKey = users(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [queryKey[0], queryKey[1], queryKey[2]],
      mutationFn: updateOwnerSettings(p),
      optimistic: (data, variables) => ({
        ...data,
        ownerSettings: {
          ...patchObject(
            data.ownerSettings,
            pickDto(AdvertiserAccount.OwnerSettings.UpdateInputDto, variables),
          ),
        },
      }),
    },
    opts,
  );
};

///////////////

export type UpdateUserOpts = MutateOpts<typeof updateUser, typeof users>;

export const useUpdateUser = (
  p: SnakeIdObj<'advertiser_account' | 'user'>,
  opts?: UseMutationOpts<UpdateUserOpts>,
) => {
  const queryKey = users(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [queryKey[0], queryKey[1], queryKey[2], p.user_id],
      mutationFn: updateUser(p),
      optimistic: (data, variables) => ({
        ...data,
        users: data.users.map((user) => ({
          ...user,
          permission: {
            ...patchObject(
              user.permission,
              pickDto(
                AdvertiserAccount.User.UpdatePermissionInputDto,
                variables,
              ),
            ),
          },
        })),
      }),
    },
    opts,
  );
};

///////////////

export type DeleteUserOpts = MutateOpts<typeof deleteUser, typeof users>;

export const useDeleteUser = (
  p: SnakeIdObj<'advertiser_account' | 'user'>,
  opts?: UseMutationOpts<DeleteUserOpts>,
) => {
  const queryKey = users(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [queryKey[0], queryKey[1], queryKey[2], p.user_id],
      mutationFn: deleteUser(p),
      optimistic: (data) => ({
        ...data,
        users: data.users.filter((user) => user.user_id !== p.user_id),
      }),
    },
    opts,
  );
};
